import React, { useCallback, useState } from 'react';

import { IconButton, Skeleton } from '@mui/material';
import clsx from 'clsx';
import { Settings } from 'lucide-react';
import { useSnackbar } from 'notistack';
import { AlineTooltip } from 'src/components/generic/AlineTooltip';
import {
  ConfirmationDialog,
  ConfirmationDialogVariant,
} from 'src/components/generic/ConfirmationDialog';
import { Apollo } from 'src/gql/generated';
import { makeStyles } from 'tss-react/mui';

import { getExternalToolkitMutationErrorMessage } from './external-toolkit-errors';
import { INTEGRATION_ROW_ACTION_CLASS } from './IntegrationActionButton';

interface ExternalToolkitPolicyPanelProps {
  readonly policy: Apollo.ExternalToolkitPolicyRowSimpleFragment;
}

interface OrgPolicyDialogProps {
  readonly allow: boolean;
  readonly name: string;
  readonly onClose: () => void;
  readonly onConfirm: () => void;
  readonly open: boolean;
}

const useStyles = makeStyles()(theme => ({
  failed: {
    color: theme.palette.text.secondary,
  },
  settingsButton: {
    color: theme.palette.text.secondary,
  },
}));

function getAllowCopy(name: string): string {
  return `${name} is off for everyone in your org. Click to enable.`;
}

function getDisableCopy(name: string): string {
  return `${name} is enabled for everyone in your org. Click to disable.`;
}

const OrgPolicyDialog = React.memo(function OrgPolicyDialog({
  allow,
  name,
  onClose,
  onConfirm,
  open,
}: OrgPolicyDialogProps): JSX.Element {
  return (
    <ConfirmationDialog
      message={allow ? getAllowCopy(name) : getDisableCopy(name)}
      open={open}
      primaryText={allow ? `Allow ${name}` : 'Disable for Everyone'}
      title={`${name} organization access`}
      variant={allow ? ConfirmationDialogVariant.Info : ConfirmationDialogVariant.Warn}
      onClickPrimary={onConfirm}
      onClose={onClose}
    />
  );
});

/**
 * Organization allow/block, opened from a settings icon on the toolkit row.
 *
 * Allow Gmail and Disconnect used to sit next to each other as the same pill.
 * One is for everyone in the org; the other is this person's account. The gear
 * keeps org policy out of that slot and fades in with Connect / Disconnect.
 */
function ExternalToolkitPolicyPanelView({ policy }: ExternalToolkitPolicyPanelProps): JSX.Element {
  const { classes } = useStyles();
  const { closeSettings, confirmPolicy, dialogAllow, loading, openSettings, settingsOpen } =
    usePolicyAction(policy);
  return (
    <>
      <AlineTooltip arrow placement="top" title="Organization access">
        <span>
          <IconButton
            aria-label={`${policy.name} organization settings`}
            className={clsx(INTEGRATION_ROW_ACTION_CLASS, classes.settingsButton)}
            disabled={loading}
            size="small"
            onClick={openSettings}
          >
            <Settings size={20} />
          </IconButton>
        </span>
      </AlineTooltip>
      <OrgPolicyDialog
        allow={dialogAllow}
        name={policy.name}
        open={settingsOpen}
        onClose={closeSettings}
        onConfirm={confirmPolicy}
      />
    </>
  );
}

function usePolicyAction(policy: Apollo.ExternalToolkitPolicyRowSimpleFragment) {
  const { enqueueSnackbar } = useSnackbar();
  const [setPolicy, { loading }] = Apollo.useSetExternalToolkitPolicyMutation({
    awaitRefetchQueries: true,
    refetchQueries: [Apollo.GetExternalToolkitsDocument, Apollo.GetExternalToolkitPoliciesDocument],
  });
  const [settingsOpen, setSettingsOpen] = useState(false);
  /**
   * The direction the open dialog will write, taken once when it opens. The
   * mutation refetches the policies and the dialog stays up until the write
   * resolves, so reading `policy.allowed` at render time would turn a
   * successful Allow into a Disable prompt in front of the admin.
   */
  const [dialogAllow, setDialogAllow] = useState(false);
  const applyPolicy = useCallback(
    async (allowed: boolean) => {
      try {
        await setPolicy({ variables: { allowed, toolkit: policy.slug } });
      } catch (error) {
        enqueueSnackbar(
          getExternalToolkitMutationErrorMessage(
            error,
            `Could not update ${policy.name} organization access. Try again.`
          ),
          { variant: 'error' }
        );
      }
    },
    [enqueueSnackbar, policy, setPolicy]
  );

  const openSettings = useCallback(() => {
    setDialogAllow(!policy.allowed);
    setSettingsOpen(true);
  }, [policy.allowed]);
  const closeSettings = useCallback(() => setSettingsOpen(false), []);
  const confirmPolicy = useCallback(() => applyPolicy(dialogAllow), [applyPolicy, dialogAllow]);

  return {
    closeSettings,
    confirmPolicy,
    dialogAllow,
    loading,
    openSettings,
    settingsOpen,
  };
}

export const ExternalToolkitPolicyPanel = React.memo(ExternalToolkitPolicyPanelView);

/**
 * Stands in on the toolkit row while an admin's policy is still loading, or
 * once it has failed.
 *
 * The panel needs two round trips before it can render, so an admin briefly
 * saw a Gmail row with no organization control at all. Absence reads as "this
 * org has no such setting" rather than "not loaded yet". A failed fetch lands
 * in the same slot, so it says so rather than going on claiming to be checking.
 */
export function ExternalToolkitPolicyPanelPlaceholder({
  failed = false,
}: {
  readonly failed?: boolean;
}): JSX.Element {
  const { classes } = useStyles();
  if (failed)
    return (
      <AlineTooltip
        arrow
        placement="top"
        title="We could not check who may use this integration. Refresh this page to try again."
      >
        <span className={clsx(INTEGRATION_ROW_ACTION_CLASS, classes.failed)}>
          <IconButton disabled aria-label="Organization settings unavailable" size="small">
            <Settings size={20} />
          </IconButton>
        </span>
      </AlineTooltip>
    );

  return (
    <Skeleton className={INTEGRATION_ROW_ACTION_CLASS} height={32} variant="circular" width={32} />
  );
}
